import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signIn, completeNewPassword } from '@/api/auth';
import { useSession } from '@/hooks/useSession';
import { useNotification } from '@/hooks/useNotification';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';

export default function LoginPage() {
  const { initiateSession, userHasAuthenticated, updateLoginProcessOngoing } = useSession();
  const navigate = useNavigate();
  const history = { push: navigate };
  const { notify } = useNotification();

  // Login form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  // New password challenge state
  const [requiresNewPassword, setRequiresNewPassword] = useState(false);
  const [signInUser, setSignInUser] = useState<unknown>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [newPwLoading, setNewPwLoading] = useState(false);
  const [newPwError, setNewPwError] = useState(false);

  async function handleLogin(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(false);
    try {
      const result = await signIn(email, password);
      if (result.challengeName === 'NEW_PASSWORD_REQUIRED') {
        setRequiresNewPassword(true);
        setSignInUser(result);
      } else {
        initiateSession();
        userHasAuthenticated(true);
        updateLoginProcessOngoing(true);
      }
    } catch {
      notify({ id: String(Date.now()), type: 'error', message: 'Could not log in' });
      setError(true);
    } finally {
      setLoading(false);
    }
  }

  async function handleNewPasswordSubmit(e: FormEvent) {
    e.preventDefault();
    if (newPassword !== confirmNewPassword) {
      notify({ id: String(Date.now()), type: 'info', message: 'Passwords do not match' });
      return;
    }
    setNewPwLoading(true);
    setNewPwError(false);
    try {
      await completeNewPassword(signInUser, newPassword);
      initiateSession();
      userHasAuthenticated(true);
      history.push('/in/folder/root');
    } catch {
      notify({ id: String(Date.now()), type: 'error', message: 'Could not perform action' });
      setNewPwError(true);
    } finally {
      setNewPwLoading(false);
    }
  }

  const loginDisabled = email.length === 0 || password.length === 0;
  const newPwDisabled = newPassword.length === 0 || confirmNewPassword.length === 0;

  if (requiresNewPassword) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="w-full max-w-md rounded-2xl border border-border/60 bg-card p-8 shadow-xl">
          <h2 className="mb-6 text-xl font-semibold">Renew password</h2>
          <form onSubmit={handleNewPasswordSubmit} className="space-y-4">
            <div>
              <label htmlFor="newPassword" className="mb-1 block text-sm font-medium text-accent-foreground">
                Password
              </label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoFocus
              />
            </div>
            <div>
              <label htmlFor="confirmNewPassword" className="mb-1 block text-sm font-medium text-accent-foreground">
                Confirm password
              </label>
              <Input
                id="confirmNewPassword"
                type="password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
              />
            </div>
            <Button type="submit" disabled={newPwDisabled || newPwLoading} className="w-full">
              {newPwLoading && <Spinner size="sm" />}
              {newPwError ? 'Try again' : 'Renew'}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md rounded-2xl border border-border/60 bg-card p-8 shadow-xl">
        <h2 className="mb-6 text-xl font-semibold">Login</h2>
        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label htmlFor="email" className="mb-1 block text-sm font-medium text-accent-foreground">
              Email
            </label>
            <Input
              id="email"
              type="text"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
            />
          </div>
          <div>
            <label htmlFor="password" className="mb-1 block text-sm font-medium text-accent-foreground">
              Password
            </label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <Button type="submit" disabled={loginDisabled || loading} className="w-full">
            {loading && <Spinner size="sm" />}
            {error ? 'Try again' : 'Login'}
          </Button>
        </form>
        <div className="mt-4 text-center">
          <Link to="/forgotPassword" className="text-sm text-primary hover:underline">
            Forgot your password?
          </Link>
        </div>
      </div>
    </div>
  );
}
